import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { ArrowLeft, Loader2, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import MobileAppLayout from "@/components/app/MobileAppLayout";
import { useAppMode } from "@/hooks/useAppMode";
import { supabase } from "@/integrations/supabase/client";

interface FormData {
  newPassword: string;
  confirmPassword: string;
}

const AppChangePassword = () => {
  const navigate = useNavigate();
  const { provider } = useAppMode();
  const brandColor = provider?.brand_primary_color || undefined;

  const [saving, setSaving] = useState(false);
  const [show, setShow] = useState(false);
  const [statusMsg, setStatusMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const { register, handleSubmit, reset, formState: { errors } } = useForm<FormData>();

  const onSubmit = async (form: FormData) => {
    setStatusMsg(null);
    if (form.newPassword !== form.confirmPassword) {
      setStatusMsg({ type: "error", text: "Passwords don't match." });
      return;
    }
    if (form.newPassword.length < 6) {
      setStatusMsg({ type: "error", text: "Password must be at least 6 characters." });
      return;
    }
    setSaving(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: form.newPassword });
      if (error) throw error;
      setStatusMsg({ type: "success", text: "Password updated." });
      reset();
    } catch (err) {
      setStatusMsg({ type: "error", text: (err as Error).message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <MobileAppLayout title="Change password">
      <div className="px-4 py-4 space-y-4">
        <Button
          variant="ghost"
          size="sm"
          className="-ml-2"
          onClick={() => navigate("/app/profile")}
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </Button>

        <h1 className="text-2xl font-bold tracking-tight">Change password</h1>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <Card>
            <CardContent className="p-4 space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="newPassword">New password</Label>
                <div className="relative">
                  <Input
                    id="newPassword"
                    type={show ? "text" : "password"}
                    {...register("newPassword", { required: true, minLength: 6 })}
                  />
                  <button
                    type="button"
                    onClick={() => setShow((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                    tabIndex={-1}
                  >
                    {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">At least 6 characters.</p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="confirmPassword">Confirm new password</Label>
                <Input
                  id="confirmPassword"
                  type={show ? "text" : "password"}
                  {...register("confirmPassword", { required: true })}
                />
              </div>
            </CardContent>
          </Card>

          {statusMsg && (
            <div
              className={
                statusMsg.type === "success"
                  ? "rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700"
                  : "rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
              }
            >
              {statusMsg.text}
            </div>
          )}

          <Button
            type="submit"
            className="w-full h-12"
            disabled={saving}
            style={brandColor ? { backgroundColor: brandColor } : undefined}
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
          </Button>
        </form>
      </div>
    </MobileAppLayout>
  );
};

export default AppChangePassword;
